export type AuthUser = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    roles: string[];
};

export type LoginCredentials = {
    email: string;
    password: string;
};

export type AuthChallenge = {
    kind: 'mfaCode' | 'newPasswordRequired';
    token: string;
};

export type SignInResult =
    | { status: 'authenticated'; user: AuthUser }
    | { status: 'challenge'; challenge: AuthChallenge }
    | { status: 'redirecting' };

export type AuthErrorCode =
    | 'invalidCredentials'
    | 'passwordResetRequired'
    | 'sessionExpired'
    | 'network'
    | 'unavailable'
    | 'notSupported'
    | 'unknown';

export class AuthError extends Error {
    public constructor(
        public readonly code: AuthErrorCode,
        message?: string,
        options?: ErrorOptions
    ) {
        super(message ?? code, options);
        this.name = 'AuthError';
    }
}

export const toAuthError = (error: unknown): AuthError =>
    error instanceof AuthError ? error : new AuthError('unknown', 'Unexpected authentication error', { cause: error });

export type AuthStateListener = (user: AuthUser | null) => void;

export interface AuthClient {
    getCurrentUser(): Promise<AuthUser | null>;
    getAccessToken(): Promise<string | null>;
    signIn(credentials?: LoginCredentials): Promise<SignInResult>;
    completeSignIn?(input: CompleteSignInInput): Promise<SignInResult>;
    signOut(): Promise<void>;
    subscribe?(listener: AuthStateListener): () => void;
}

export type CompleteSignInInput =
    | { kind: 'redirectCallback'; url: string }
    | { kind: 'challengeResponse'; challenge: AuthChallenge; value: string };
